/* In-page collection tabs (owner, 2026-09-26). The four tab groups and which
 * collections belong to them are defined in
 * sections/sondos-collection-header.liquid; this file is group-agnostic.
 *
 * The tabs are plain links, and every one of them is a real, server-rendered
 * page — a collection URL, sometimes with a view (`?view=tab-all`,
 * `?view=tab-shop`) so the header renders the right tab row on a reload.
 * Without this file a tap simply navigates there. This file only makes that
 * switch instant: she stays where she is, the product area below the tabs is
 * replaced, and the H1 / <title> follow the tab (data-sct-title).
 *
 * How the switch stays correct:
 *  - The section is fetched through the Section Rendering API for the tab's own
 *    URL, so the grid, the count, the filters and the auto-load sentinel
 *    (whose next-url keeps `view`) all belong to that category.
 *  - The address bar is moved to the tab's URL (replaceState — a tab is not a
 *    history step) BEFORE the grid is swapped. assets/sondos-collection.js keys
 *    its stored pages and its Back restore on the URL and re-arms when it sees
 *    a new #product-grid, so the auto-load, Back and reload all follow the tab
 *    and never mix «الكل» pages with a category's pages.
 *  - The swap goes through facets.js's own renderers — the exact path a sort or
 *    filter change already takes, which sondos-collection.js is built around.
 *  - The hidden `view` field in the two facet forms is kept in step, so a sort
 *    or filter inside a tab stays inside it (snippets/facets.liquid renders the
 *    same field on a reload).
 *
 * Short-lived, bounded HTML cache; intent and two adjacent tabs warmed only
 * on an unrestricted connection after load. No image preloads. A cached
 * response is parsed once and shared by the facet renderers.
 * While waiting the old grid is inert; delayed progress never resizes the
 * rail or grid. Only the latest interaction owns the result.
 *
 * The pinned row (2026-10-15, phone only). Once the rail has scrolled out of
 * view, the pinned facets bar stops spending its row on «تصفية وترتيب» + the
 * count and carries a copy of these tabs beside an icon-only filter trigger.
 * Measured before deciding (GA4, 28 days): ~2% of collection views filter and
 * ~2% sort, while tab switches ran ~11 per 100 views — from a rail visible
 * only at the very top of an auto-loading list with no footer. Same height,
 * so nothing under the bar moves; at the top of the page nothing changes.
 * The copy is built here, from the rail, so there is one source of truth;
 * without JS there is no copy and the bar is the old one. A tab tapped in the
 * pinned row opens the new category from its first product, right under the
 * bar, which stays as it is (owner 2026-10-16) — see `landUnderBar`.
 */
(function () {
  'use strict';

  var rail = document.querySelector('[data-sct]');
  if (!rail || !window.fetch || !window.URL || !history.replaceState) return;

  var view = rail.getAttribute('data-sct-view') || '';
  var PENDING_MS = 160;
  var cache = {};
  var ticket = 0;
  var pendingTimer = 0;
  var pendingHref = '';
  var CACHE_MS = 5 * 60 * 1000;
  var CACHE_LIMIT = 6;
  var status = document.createElement('p');
  status.className = 'sct-status visually-hidden';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.setAttribute('aria-atomic', 'true');
  rail.parentNode.appendChild(status);

  /* The pinned copy: same classes (look, LTR track with reversed `order`, the
     screen-sized tab widths), none of the rail's ids or data hooks. It lives
     in `.mfbar`, which facets.js never re-renders — it only replaces pieces
     inside it — so filters, sort and tab swaps all leave it in place. */
  var bar = document.querySelector('.facets-wrapper');
  var mfbar = bar && bar.querySelector('.mfbar');
  var mini = null;
  if (mfbar) {
    mini = rail.cloneNode(true);
    ['id', 'data-sct', 'data-sct-view', 'data-sct-loading', 'data-sct-pending', 'data-sct-preserve-filters', 'data-sct-default-sort'].forEach(function (name) {
      mini.removeAttribute(name);
    });
    mini.classList.add('sch__nav-list--mini');
    var miniNav = document.createElement('nav');
    miniNav.className = 'sct-mini';
    var label = document.querySelector('.sch__nav-label');
    miniNav.setAttribute('aria-label', (label && label.textContent.trim()) || document.title);
    miniNav.appendChild(mini);
    miniNav.inert = true;
    mfbar.appendChild(miniNav);
  }
  var lists = mini ? [rail, mini] : [rail];

  function owns(link) {
    return lists.some(function (list) { return list.contains(link); });
  }

  function setPending(on) {
    lists.forEach(function (list) {
      if (on) list.setAttribute('data-sct-pending', '');
      else list.removeAttribute('data-sct-pending');
    });
  }

  function keyOf(href) {
    var u = new URL(href, location.href);
    return u.pathname + '|' + (u.searchParams.get('view') || '');
  }

  function requestKey(href) {
    var u = new URL(href, location.href);
    ['preview_theme_id', '_fd', '_ab', 'pb', 'section_id'].forEach(function (key) { u.searchParams.delete(key); });
    u.searchParams.sort();
    return u.pathname + '?' + u.searchParams.toString();
  }

  function tabHref(link) {
    var u = new URL(link.href, location.href);
    if (rail.hasAttribute('data-sct-preserve-filters')) {
      var current = new URL(location.href);
      current.searchParams.forEach(function (value, name) {
        if (name.indexOf('filter.') === 0) u.searchParams.append(name, value);
      });
      var sort = current.searchParams.get('sort_by');
      if (sort) u.searchParams.set('sort_by', sort);
      else if (keyOf(link.href) !== keyOf(links()[0].href)) u.searchParams.set('sort_by', rail.getAttribute('data-sct-default-sort'));
    }
    return u.href;
  }

  var shown = keyOf(location.href);
  /* The description in the header was rendered for this tab only. */
  var origin = shown;

  function links() {
    return Array.prototype.slice.call(rail.querySelectorAll('a[data-sct-link]'));
  }

  /* Both lists hold the same tabs; a tab is the same tab in either. */
  function mark(link) {
    var key = keyOf(link.href);
    lists.forEach(function (list) {
      list.querySelectorAll('a[data-sct-link]').forEach(function (a) {
        if (keyOf(a.href) === key) a.setAttribute('aria-current', 'page');
        else a.removeAttribute('aria-current');
      });
    });
  }

  /* A chip half past its list's edge is brought fully into that list's view —
     the list's own scrollLeft only, never the page. Both lists follow, so the
     rail is right when she scrolls back up; the pinned one is laid out even
     while hidden (see `centre`). A list with no box (≥750) is skipped. */
  function reveal(link) {
    var key = keyOf(link.href);
    lists.forEach(function (list) {
      var r = list.getBoundingClientRect();
      if (!r.width) return;
      var twin = null;
      list.querySelectorAll('a[data-sct-link]').forEach(function (a) { if (keyOf(a.href) === key) twin = a; });
      if (!twin) return;
      var a = twin.getBoundingClientRect();
      if (a.left < r.left) list.scrollLeft -= (r.left - a.left) + 16;
      else if (a.right > r.right) list.scrollLeft += (a.right - r.right) + 16;
    });
  }

  function markByLocation() {
    var here = keyOf(location.href);
    var match = null;
    links().forEach(function (a) { if (keyOf(a.href) === here) match = a; });
    if (match) mark(match);
  }

  function collectionBox() {
    var c = document.getElementById('ProductGridContainer');
    return c && c.querySelector('.collection');
  }

  function sectionId() {
    var c = document.getElementById('ProductGridContainer');
    var s = c && c.closest('.shopify-section');
    return s && s.id ? s.id.replace(/^shopify-section-/, '') : '';
  }

  function fetchSection(href) {
    var key = requestKey(href);
    var old = cache[key];
    if (old && Date.now() - old.time < CACHE_MS) return old.promise;
    var id = sectionId();
    if (!id) return Promise.reject(new Error('no section'));
    var url = new URL(href, location.href);
    url.searchParams.set('section_id', id);
    var controller = window.AbortController ? new AbortController() : null;
    var entry = { time: Date.now(), controller: controller };
    var timeout;
    var request = fetch(url.href, { credentials: 'same-origin', signal: controller ? controller.signal : undefined })
      .then(function (res) {
        if (!res.ok) throw new Error('HTTP ' + res.status);
        return res.text();
      })
      .then(function (html) {
        if (html.indexOf('id="ProductGridContainer"') === -1) throw new Error('no grid');
        return html;
      });
    var p = Promise.race([request, new Promise(function (_, reject) {
      timeout = setTimeout(function () {
        if (controller) controller.abort();
        reject(new Error('section timeout'));
      }, 12000);
    })]);
    entry.promise = p;
    cache[key] = entry;
    var keys = Object.keys(cache);
    while (keys.length > CACHE_LIMIT) {
      var first = keys.shift();
      if (cache[first].controller) cache[first].controller.abort();
      delete cache[first];
    }
    p.then(function () {
      clearTimeout(timeout);
      entry.controller = null;
    }, function () {
      clearTimeout(timeout);
      if (cache[key] === entry) delete cache[key];
    });
    return p;
  }

  function canWarm() {
    var connection = navigator.connection;
    return !document.hidden && !(window.Shopify && window.Shopify.designMode) &&
      !(connection && (connection.saveData || /(^|-)2g$|3g/.test(connection.effectiveType || '')));
  }

  function warm(link) {
    if (!link || !canWarm() || pendingHref || keyOf(link.href) === shown) return Promise.resolve();
    return fetchSection(tabHref(link)).catch(function () {});
  }

  /* One small pair per document, sequential and after the initial load. */
  function warmNeighbours() {
    setTimeout(function () {
      var work = function () {
        if (!canWarm() || pendingHref) return;
        var all = links();
        var index = all.findIndex(function (a) { return keyOf(a.href) === shown; });
        var next = all[index + 1];
        var other = all[index - 1] || all[index + 2];
        warm(next).then(function () { return warm(other); });
      };
      if (window.requestIdleCallback) window.requestIdleCallback(work, { timeout: 2000 });
      else work();
    }, 2000);
  }

  /* The hidden `view` field in the two submitted facet forms. */
  function syncViewField(target) {
    ['FacetFiltersForm', 'FacetFiltersFormMobile'].forEach(function (id) {
      var form = document.getElementById(id);
      if (!form) return;
      var field = form.querySelector('input[data-sct-view]');
      if (target) {
        if (!field) {
          field = document.createElement('input');
          field.type = 'hidden';
          field.name = 'view';
          field.setAttribute('data-sct-view', '');
          form.insertBefore(field, form.firstChild);
        }
        field.value = target;
      } else if (field) {
        field.remove();
      }
    });
  }

  /* facets.js re-renders the mobile sort sheet but not the desktop sort
     select, so a sort chosen under «الكل» would stay displayed over a tab that
     opened in its default order. Take the value from the fetched page. */
  function syncSort(doc) {
    var value = '';
    var sel = doc.querySelector('select[name="sort_by"] option[selected]');
    var radio = doc.querySelector('input[name="sort_by"][checked]');
    if (sel) value = sel.value;
    else if (radio) value = radio.value;
    if (!value) return;
    document.querySelectorAll('select[name="sort_by"]').forEach(function (s) { s.value = value; });
    document.querySelectorAll('input[name="sort_by"]').forEach(function (r) { r.checked = r.value === value; });
  }

  /* The H1 is always the selected tab's collection title (owner, 2026-09-26);
     «الكل» carries the parent's. The header section is not re-rendered on an
     in-page switch, so the title comes from the link. The visually hidden
     prefix inside the H1 is kept; <title> keeps its store suffix. */
  function syncTitle(link) {
    var title = link.getAttribute('data-sct-title');
    if (!title) return;
    var h1 = document.querySelector('.sch__title');
    if (h1) {
      var last = h1.lastChild;
      if (last && last.nodeType === 3) last.textContent = title;
      else h1.appendChild(document.createTextNode(title));
    }
    var parts = document.title.split(' – ');
    document.title = parts.length > 1 ? title + ' – ' + parts.slice(1).join(' – ') : title;
    /* A description belongs to the collection it was rendered for: shown on
       that tab, hidden on every other one, back again on return. */
    var desc = document.querySelector('.sch__desc');
    if (desc) desc.hidden = keyOf(link.href) !== origin;
  }

  /* One frame, then a task of its own: lets the browser paint what the tap
     already changed before the heavy DOM work starts. */
  function afterPaint(cb) {
    requestAnimationFrame(function () { setTimeout(cb, 0); });
  }

  function clearPending() {
    clearTimeout(pendingTimer);
    pendingHref = '';
    setPending(false);
    status.classList.add('visually-hidden');
    var container = document.getElementById('ProductGridContainer');
    if (container) container.removeAttribute('aria-busy');
    var grid = document.getElementById('product-grid');
    if (grid) grid.removeAttribute('inert');
    var box = collectionBox();
    if (box) box.classList.remove('is-tab-pending');
  }

  function swap(href, html) {
    /* facets.js declares `class FacetFiltersForm` at the top level of a
       classic script: a global binding, but NOT a property of window. */
    var FF = typeof FacetFiltersForm !== 'undefined' ? FacetFiltersForm : null;
    if (!FF || !FF.renderProductGridContainer) throw new Error('no facets');
    var doc = new DOMParser().parseFromString(html, 'text/html');
    if (!doc.getElementById('ProductGridContainer') || !doc.getElementById('ProductCount')) throw new Error('incomplete section');
    var u = new URL(href, location.href);
    var search = u.search.slice(1);
    /* URL first: sondos-collection.js reads it the moment the new grid lands. */
    history.replaceState({ searchParams: search }, '', u.pathname + u.search + u.hash);
    FF.searchParamsPrev = search;
    syncViewField(u.searchParams.get('view') || '');
    FF.renderFilters(html, null, doc);
    FF.renderProductGridContainer(html, doc);
    FF.renderProductCount(html, doc);
    syncSort(doc);
    if (FF.confirmControls) FF.confirmControls();
    shown = keyOf(href);
  }

  /* The grid's top just under the pinned bar: the bar sits on its sticky line
     (`--skh-stick`, 0 while the header is hidden) and the first row starts
     below it. The header is told this is not a swipe (sondos-header.liquid,
     `sondos:scroll-jump`), so it neither slides in nor out. */
  /* The first row keeps the air it opens the page with under the bar, read
     once while the bar is still in flow (16px at 390); never flush against
     the hairline. */
  var restGap = 16;
  (function () {
    var first = document.querySelector('#product-grid > li');
    if (!bar || !first) return;
    var g = Math.round(first.getBoundingClientRect().top - bar.getBoundingClientRect().bottom);
    if (window.scrollY === 0 && g >= 0 && g <= 48) restGap = g;
  })();

  function landUnderBar() {
    var first = document.querySelector('#product-grid > li') || document.getElementById('ProductGridContainer');
    if (!first || !bar) return;
    /* The header writes `--skh-stick` inline on the bar itself ([data-skh-stick],
       2026-10-22 — a root write restyled the whole document). */
    var line = parseFloat(bar.style.getPropertyValue('--skh-stick')) || 0;
    var y = Math.max(0, Math.round(first.getBoundingClientRect().top + (window.scrollY || window.pageYOffset || 0) - line - bar.offsetHeight - restGap));
    if (Math.abs(y - (window.scrollY || window.pageYOffset || 0)) < 1) return;
    document.dispatchEvent(new CustomEvent('sondos:scroll-jump'));
    window.scrollTo(0, y);
  }

  function go(link, toTop) {
    var href = tabHref(link);
    var mine = ++ticket;
    clearPending();
    var FF = typeof FacetFiltersForm !== 'undefined' ? FacetFiltersForm : null;
    if (FF && FF.cancelRender) FF.cancelRender();
    mark(link);
    reveal(link);
    pendingHref = href;
    var container = document.getElementById('ProductGridContainer');
    if (container) container.setAttribute('aria-busy', 'true');
    var grid = document.getElementById('product-grid');
    if (grid) grid.setAttribute('inert', '');
    Object.keys(cache).forEach(function (key) {
      if (key !== requestKey(href) && cache[key].controller) {
        cache[key].controller.abort();
        delete cache[key];
      }
    });
    pendingTimer = setTimeout(function () {
      if (mine !== ticket) return;
      setPending(true);
      status.textContent = (rail.getAttribute('data-sct-loading') || '') + ' ' + (link.getAttribute('data-sct-title') || link.textContent);
      var box = collectionBox();
      if (box) box.classList.add('is-tab-pending');
    }, PENDING_MS);

    fetchSection(href).then(function (html) {
      if (mine !== ticket) return;
      /* After the next paint, never in the tap's own task. The swap re-renders
         the grid (24 cards + filters + count: a 150–380ms task at 4× CPU), and
         when the section came from the cache — the warmed neighbours, a tab
         already visited — the promise was already settled, so the swap ran
         before the tap's first frame: input → next paint 200ms on the rail,
         440ms from the pinned row (measured 2026-10-22, 390 / 4× CPU). The
         chip is already marked current (`mark`, synchronous above), so that is
         what the first frame shows; the swap follows in its own task. */
      afterPaint(function () {
      if (mine !== ticket) return;
      clearPending();
      try {
        /* From the pinned row: the new category opens from its first product,
           right under the bar, and the bar stays as it is (owner 2026-10-16 —
           the first pass went back to the top of the page and brought the
           whole header back). The jump comes first, in the same task as the
           swap (one change on screen, not two): replacing the grid while the
           screen sat in the middle of it cost 3.7–4.6s to the next paint at 4×
           CPU, against 1.5s for the same swap at the grid's top (measured
           2026-10-15). Re-aimed after the swap in case the new title wrapped
           differently above. */
        if (toTop) landUnderBar();
        swap(href, html);
        syncTitle(link);
        if (toTop) landUnderBar();
        condense();
        /* ...and the new cards ease in, so the jump reads as turning to a
           new page rather than a cut. Opacity only (composited), after the
           swap, never on the rail path where nothing moves. */
        var landed = toTop && document.getElementById('product-grid');
        if (landed && landed.animate && !(window.matchMedia && matchMedia('(prefers-reduced-motion: reduce)').matches)) {
          landed.animate([{ opacity: 0.25 }, { opacity: 1 }], { duration: 220, easing: 'ease-out' });
        }
        var count = document.getElementById('ProductCount');
        status.textContent = (link.getAttribute('data-sct-title') || link.textContent) + (count ? '، ' + count.textContent.trim() : '');
      } catch (e) {
        window.location.href = href;
      }
      });
    }).catch(function () {
      if (mine !== ticket) return;
      clearPending();
      window.location.href = href;
    });
  }

  function onIntent(event) {
    var link = event.target.closest('a[data-sct-link]');
    if (!link || !owns(link) || link.hasAttribute('aria-current')) return;
    if (event.type === 'pointerover' && event.pointerType !== 'mouse') return;
    warm(link);
  }

  function onClick(event) {
    var link = event.target.closest('a[data-sct-link]');
    if (!link || !owns(link)) return;
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    if (keyOf(link.href) === shown) {
      ++ticket;
      clearPending();
      status.textContent = '';
      mark(link);
      reveal(link);
      return;
    }
    if (pendingHref === tabHref(link)) return;
    go(link, !!mini && mini.contains(link));
  }

  /* iOS only applies :active (the pressed tint in the page sheet) to an
     element with a touch listener on its path; passive, it costs nothing. */
  function noop() {}
  lists.forEach(function (list) {
    list.addEventListener('pointerover', onIntent, { passive: true });
    list.addEventListener('focusin', onIntent);
    list.addEventListener('pointerdown', onIntent, { passive: true });
    list.addEventListener('touchstart', noop, { passive: true });
    list.addEventListener('click', onClick);
  });

  /* Condensed = the rail is fully out of view: above the screen, or under
     the pinned header (`--skh-stick` is the header's bottom edge while it is
     shown, 0 while hidden; read from the inline style the header script
     writes, so no style recalc). At that point the facets bar is pinned or
     within the rail's 24px of being so — the two rows are never both whole on
     screen. Read in one rAF per scroll burst; the class only flips on a
     change, and nothing in the bar changes height when it does. */
  var phone = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
  var condensed = false;
  var queued = false;
  var settleTimer = 0;

  /* The pinned list is laid out from load and only shown/hidden with
     visibility (page sheet, R8): iOS builds the native scroller of an
     overflow box when it first renders, and a list that went from
     display:none to visible at the moment it pinned — usually mid-glide —
     took the owner several swipes to move on her iPhone (2026-10-16).
     So it keeps its offset too. Parked once to its right edge like the
     rail (sondos-collection-header.liquid), on fonts.ready again since
     Almarai lands after first paint; after that only `centre` moves it. */
  function centre() {
    var on = mini.querySelector('[aria-current]');
    if (!on) return;
    var a = on.getBoundingClientRect();
    var r = mini.getBoundingClientRect();
    if (a.left < r.left || a.right > r.right) mini.scrollLeft += (a.left + a.width / 2) - (r.left + r.width / 2);
  }

  /* About a third of the next tab shows at rest (owner 2026-10-10: the cut
     tab is how she knows the row goes on). Labels differ in length from one
     group to the next («حبوب» … «جفاف وتقشر», «العناية بالبشرة»), so no fixed
     width lands that at every phone width — measured, a formula gave 25–30%
     on two groups and 0–95% on the other three. Instead: the tabs' own
     widths are read and the same few pixels of padding are added to (or, at
     most 4px a side, taken from) every tab so that k whole tabs + 0.3 of the
     next fill the row, k as large as fits. Uniform, so the row still reads
     as one set; taking a little rather than adding a lot keeps the long-label
     rows from ballooning (shop: 108–158px tabs with add-only). Re-fitted each
     time the row appears, when the real face has certainly landed. */
  /* 2026-10-16, owner's iPhone video: the row appeared on «الكل», then slid
     ~73px and lost it, leaving «وصل حديثًا» cut against the icon. Scrolling
     down collapses Safari's toolbar, which fires `resize` just as the row
     pins; this used to strip the padding to measure, the list shrank, the
     browser clamped its scroll offset, and the padding came back with the
     offset still clamped. Now: the natural widths are derived from the
     current ones (nothing is removed), the padding is only rewritten when it
     really changes, the offset is kept from the row's start (its right end),
     and only a change of width re-fits. */
  var PAD = 12, GAP = 8, PEEK = 0.3, TRIM = -8, BLOAT = 24;
  function fitPeek() {
    var cur = parseFloat(mini.style.getPropertyValue('--sct-pad')) || PAD;
    var widths = Array.prototype.map.call(mini.querySelectorAll('.sch__nav-link'), function (a) { return a.getBoundingClientRect().width - 2 * (cur - PAD); });
    /* What she sees: the list's right edge to the screen's left (the list
       bleeds past its box to the edge, so clientWidth is 16px short). */
    var box = mini.getBoundingClientRect();
    var room = box.right - Math.max(0, box.left);
    if (!room || !widths.length) return;
    var all = widths.reduce(function (s, w) { return s + w; }, 0) + GAP * (widths.length - 1);
    var extra = null, sum = 0;
    if (all > room) {
      for (var k = 1; k < widths.length; k++) {
        sum += widths[k - 1];
        var e = (room - sum - GAP * k - PEEK * widths[k]) / (k + PEEK);
        if (e < TRIM) break;
        extra = e;
      }
    }
    var next = extra === null ? PAD : PAD + Math.min(extra, BLOAT) / 2;
    if (Math.abs(next - cur) < 0.25) return;
    var fromEnd = mini.scrollWidth - mini.clientWidth - mini.scrollLeft;
    if (next === PAD) mini.style.removeProperty('--sct-pad');
    else mini.style.setProperty('--sct-pad', next.toFixed(2) + 'px');
    mini.scrollLeft = mini.scrollWidth - mini.clientWidth - fromEnd;
  }

  var fitWidth = window.innerWidth;
  function onResize() {
    if (window.innerWidth === fitWidth) return;
    fitWidth = window.innerWidth;
    fitPeek();
  }

  var touched = false;
  function park() {
    if (touched) return;
    fitPeek();
    mini.scrollLeft = mini.scrollWidth;
    centre();
  }

  function condense() {
    queued = false;
    if (!mini) return;
    var line = parseFloat(bar.style.getPropertyValue('--skh-stick')) || 0;
    var on = !!(phone && phone.matches) && rail.getBoundingClientRect().bottom <= line + 1;
    if (on === condensed) return;
    condensed = on;
    bar.classList.toggle('is-condensed', on);
    /* Invisible (opacity 0) but laid out: out of the tab order and the
       accessibility tree until it is the row on screen. */
    miniNav.inert = !on;
    /* Back to the full trigger: its label and the count ease in (R8). Only
       on this change — never on first paint. */
    clearTimeout(settleTimer);
    bar.classList.toggle('is-expanding', !on);
    if (!on) settleTimer = setTimeout(function () { bar.classList.remove('is-expanding'); }, 260);
    /* Re-fitted (fonts are in by now); then wherever she left it — or at its
       right edge if she has not touched it — unless the selected tab is out
       of its view. */
    if (on) {
      fitPeek();
      if (!touched) mini.scrollLeft = mini.scrollWidth;
      centre();
    }
  }

  function queue() {
    if (queued) return;
    queued = true;
    requestAnimationFrame(condense);
  }

  if (mini) {
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    /* The header shows or hides with no scroll of its own after the last
       event of a swipe; the bar's `top` follows it over 0.22s. */
    bar.addEventListener('transitionend', queue);
    mini.addEventListener('pointerdown', function () { touched = true; }, { passive: true, once: true });
    window.addEventListener('resize', onResize, { passive: true });
    requestAnimationFrame(park);
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(park);
    condense();
  }

  /* A sort/filter interaction supersedes a pending tab, just as another tab
     does. Conversely cancelRender prevents an old facet response winning. */
  document.addEventListener('sondos:facets-start', function () {
    ++ticket;
    clearPending();
    status.textContent = '';
    markByLocation();
  });

  /* Back/Forward that lands on a different tab than the one on screen (e.g.
     a sort pushed an entry, then the tab changed) — load that URL as a page
     rather than trying to reconcile two in-page renderers. */
  window.addEventListener('popstate', function () {
    ++ticket;
    clearPending();
    if (keyOf(location.href) !== shown) window.location.reload();
  });

  /* bfcache return: the DOM already holds the tab she left; keep the chip in
     step with the address bar in case they ever diverge. */
  window.addEventListener('pageshow', function (event) {
    if (event.persisted) markByLocation();
  });

  window.addEventListener('pagehide', function () {
    ++ticket;
    clearPending();
    markByLocation();
  });

  /* Only the unfiltered first page can seed a category tab. Captured before
     the deferred auto-load script appends further pages. */
  var initialURL = new URL(location.href);
  var clean = Array.from(initialURL.searchParams.keys()).every(function (key) {
    return ['view', 'preview_theme_id', '_fd', '_ab', 'pb'].indexOf(key) !== -1;
  });
  var initialBox = document.getElementById('ProductGridContainer');
  var initialSection = initialBox && initialBox.closest('.shopify-section');
  if (clean && initialSection) {
    cache[requestKey(location.href)] = { time: Date.now(), promise: Promise.resolve(initialSection.outerHTML) };
  }
  if (document.readyState === 'complete') warmNeighbours();
  else window.addEventListener('load', warmNeighbours, { once: true });

  if (view) syncViewField(new URL(location.href).searchParams.get('view') || '');
})();
